import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getOrgBoards, createBoard, updateBoard, deleteBoard, getOrgMembers } from '../../api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';
import Alert from '../../components/Alert';
import Loading from '../../components/Loading';
import EmptyState from '../../components/EmptyState';
import {
  PlusIcon,
  TrashIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const COLUMN_COLORS = ['#6c5ce7', '#00b894', '#fdcb6e', '#e17055', '#0984e3', '#e84393'];

export default function BoardsPage() {
  const { orgId } = useParams();
  const { user, organizations } = useAuth();
  const [clubBoard, setClubBoard] = useState(null);
  const [personalBoard, setPersonalBoard] = useState(null);
  const [boardScope, setBoardScope] = useState('club');
  const [loading, setLoading] = useState(true);
  const [showCreateBoard, setShowCreateBoard] = useState(false);
  const [showAddTask, setShowAddTask] = useState(null);
  const [showEditTask, setShowEditTask] = useState(null);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [boardTitle, setBoardTitle] = useState('');
  const [taskForm, setTaskForm] = useState({ title: '', description: '', assigneeId: '' });
  const [editTaskForm, setEditTaskForm] = useState({ title: '', description: '', assigneeId: '' });
  const [error, setError] = useState('');
  const [dragItem, setDragItem] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);

  const org = organizations.find(o => o._id === orgId);
  const maxColumns = 5;
  const canManageBoards = ['owner', 'leader', 'admin'].includes(user?.role) || !!user?.permissions?.manageBoards;
  const activeBoard = boardScope === 'club' ? clubBoard : personalBoard;
  const canCreateBoard = !activeBoard;

  useEffect(() => {
    if (canManageBoards) {
      loadBoards();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [orgId]);

  const loadBoards = async () => {
    try {
      const [clubRes, personalRes, membersRes] = await Promise.all([
        getOrgBoards(orgId, 'club'),
        getOrgBoards(orgId, 'personal'),
        getOrgMembers(orgId)
      ]);
      setClubBoard(clubRes.data[0] || null);
      setPersonalBoard(personalRes.data[0] || null);
      setOrgMembers(membersRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load boards');
    } finally {
      setLoading(false);
    }
  };

  const setScopedBoard = (scope, board) => {
    if (scope === 'club') {
      setClubBoard(board);
      return;
    }
    setPersonalBoard(board);
  };

  const handleCreateBoard = async () => {
    if (!boardTitle.trim()) return;
    try {
      const res = await createBoard({ organizationId: orgId, title: boardTitle.trim(), scope: boardScope });
      setScopedBoard(boardScope, res.data);
      setShowCreateBoard(false);
      setBoardTitle('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create board');
    }
  };

  const handleDeleteBoard = async (boardId) => {
    if (!window.confirm('Delete this board?')) return;
    try {
      await deleteBoard(boardId);
      setScopedBoard(boardScope, null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete board');
    }
  };

  const handleAddColumn = async () => {
    if (!newColumnTitle.trim() || !activeBoard) return;
    if ((activeBoard.columns?.length || 0) >= maxColumns) {
      setError(`Maximum ${maxColumns} columns allowed.`);
      return;
    }
    const newColumns = [
      ...(activeBoard.columns || []),
      { id: Date.now().toString(), title: newColumnTitle.trim(), tasks: [] }
    ];
    try {
      const res = await updateBoard(activeBoard._id, { columns: newColumns });
      setScopedBoard(boardScope, res.data);
      setShowAddColumn(false);
      setNewColumnTitle('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add column');
    }
  };

  const handleDeleteColumn = async (columnId) => {
    if (!activeBoard) return;
    if (!window.confirm('Delete this column and all its tasks?')) return;
    const newColumns = activeBoard.columns.filter(c => c.id !== columnId);
    try {
      const res = await updateBoard(activeBoard._id, { columns: newColumns });
      setScopedBoard(boardScope, res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete column');
    }
  };

  const handleAddTask = async (columnId) => {
    if (!taskForm.title.trim() || !activeBoard) return;
    const newColumns = activeBoard.columns.map(col => {
      if (col.id === columnId) {
        return {
          ...col,
          tasks: [...col.tasks, { id: Date.now().toString(), title: taskForm.title.trim(), description: taskForm.description.trim(), assigneeId: taskForm.assigneeId || null }]
        };
      }
      return col;
    });
    try {
      const res = await updateBoard(activeBoard._id, { columns: newColumns });
      setScopedBoard(boardScope, res.data);
      setShowAddTask(null);
      setTaskForm({ title: '', description: '', assigneeId: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add task');
    }
  };

  const handleDeleteTask = async (columnId, taskId) => {
    if (!activeBoard) return;
    const newColumns = activeBoard.columns.map(col => {
      if (col.id === columnId) {
        return { ...col, tasks: col.tasks.filter(t => t.id !== taskId) };
      }
      return col;
    });
    try {
      const res = await updateBoard(activeBoard._id, { columns: newColumns });
      setScopedBoard(boardScope, res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete task');
    }
  };

  const openTaskEditor = (columnId, task) => {
    setShowEditTask({ columnId, taskId: task.id });
    setEditTaskForm({ title: task.title || '', description: task.description || '', assigneeId: task.assigneeId || '' });
  };

  const handleEditTask = async () => {
    if (!activeBoard || !showEditTask || !editTaskForm.title.trim()) return;
    const { columnId, taskId } = showEditTask;
    const newColumns = activeBoard.columns.map(col => {
      if (col.id !== columnId) return col;
      return {
        ...col,
        tasks: (col.tasks || []).map(task => {
          if (task.id !== taskId) return task;
          return { ...task, title: editTaskForm.title.trim(), description: editTaskForm.description.trim(), assigneeId: editTaskForm.assigneeId || null };
        })
      };
    });

    try {
      const res = await updateBoard(activeBoard._id, { columns: newColumns });
      setScopedBoard(boardScope, res.data);
      setShowEditTask(null);
      setEditTaskForm({ title: '', description: '', assigneeId: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update task');
    }
  };

  const handleDragStart = (e, columnId, taskId) => {
    setDragItem({ columnId, taskId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    setDragOverColumn(columnId);
  };

  const handleDrop = async (e, targetColumnId) => {
    e.preventDefault();
    setDragOverColumn(null);
    if (!dragItem || !activeBoard) return;
    if (dragItem.columnId === targetColumnId) return;

    let movedTask = null;
    const newColumns = activeBoard.columns.map(col => {
      if (col.id === dragItem.columnId) {
        movedTask = col.tasks.find(t => t.id === dragItem.taskId);
        return { ...col, tasks: col.tasks.filter(t => t.id !== dragItem.taskId) };
      }
      return col;
    }).map(col => {
      if (col.id === targetColumnId && movedTask) {
        return { ...col, tasks: [...col.tasks, movedTask] };
      }
      return col;
    });

    try {
      const res = await updateBoard(activeBoard._id, { columns: newColumns });
      setScopedBoard(boardScope, res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to move task');
    }
    setDragItem(null);
  };

  const getColumnColor = (index) => COLUMN_COLORS[index % COLUMN_COLORS.length];

  if (loading) return <Loading />;

  if (!canManageBoards) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h2>Board</h2>
            <div className="subtitle">{org?.name || 'Organization'} - Kanban Board</div>
          </div>
        </div>
        <Alert
          type="error"
          message="You do not have permission to access kanban boards. Ask owner/leader/admin to grant Kanban Board Access."
          onClose={() => {}}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Board</h2>
          <div className="subtitle">{org?.name || 'Organization'} - Kanban Board</div>
        </div>
        {canCreateBoard && (
          <button className="btn btn-primary" onClick={() => {
            setBoardTitle(boardScope === 'club' ? `${org?.name || 'Organization'} Board` : 'My Board');
            setShowCreateBoard(true);
          }}>
            <PlusIcon className="icon" /> Create {boardScope === 'club' ? 'Club' : 'My'} Board
          </button>
        )}
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          className={`btn ${boardScope === 'club' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => {
            setBoardScope('club');
            setShowAddTask(null);
            setShowAddColumn(false);
          }}
        >
          <BuildingOfficeIcon className="icon" /> Club Board
        </button>
        <button
          className={`btn ${boardScope === 'personal' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => {
            setBoardScope('personal');
            setShowAddTask(null);
            setShowAddColumn(false);
          }}
        >
          <UserIcon className="icon" /> My Board
        </button>
      </div>

      {!activeBoard ? (
        <EmptyState
          icon={<ClipboardDocumentListIcon style={{ width: 48, height: 48 }} />}
          title={`No ${boardScope === 'club' ? 'Club' : 'Personal'} Board Yet`}
          description={boardScope === 'club'
            ? 'Create a shared Kanban board for your organization.'
            : 'Create your private Kanban board for this organization.'}
          action={
            <button className="btn btn-primary" onClick={() => {
              setBoardTitle(boardScope === 'club' ? `${org?.name || 'Organization'} Board` : 'My Board');
              setShowCreateBoard(true);
            }}>
              <PlusIcon className="icon" /> Create Board
            </button>
          }
        />
      ) : (
        <>
          <div className="board-selector" style={{ justifyContent: 'space-between' }}>
            <button className="board-tab active" style={{ cursor: 'default' }}>
              {activeBoard.title}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => handleDeleteBoard(activeBoard._id)}
              style={{ color: 'var(--text-muted)', padding: 4 }}
              title="Delete board"
            >
              <TrashIcon className="icon" />
            </button>
          </div>

          <div className="kanban-board">
            {activeBoard.columns?.map((column, colIndex) => (
              <div
                key={column.id}
                className="kanban-column"
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDrop={(e) => handleDrop(e, column.id)}
                style={dragOverColumn === column.id ? { borderColor: 'var(--accent)' } : {}}
              >
                <div className="kanban-column-header">
                  <span className="kanban-column-title">
                    <span className="kanban-col-dot" style={{ background: getColumnColor(colIndex) }}></span>
                    {column.title}
                    <span className="kanban-column-count">{column.tasks?.length || 0}</span>
                  </span>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleDeleteColumn(column.id)}
                    style={{ color: 'var(--text-muted)', padding: 2 }}
                    title="Delete column"
                  >
                    <XMarkIcon className="icon" style={{ width: 14, height: 14 }} />
                  </button>
                </div>
                <div className="kanban-cards">
                  {column.tasks?.map(task => {
                    const assignee = task.assigneeId ? orgMembers.find(m => m._id === task.assigneeId) : null;
                    return (
                    <div
                      key={task.id}
                      className={`kanban-card ${dragItem?.taskId === task.id ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, column.id, task.id)}
                    >
                      <div className="kanban-card-title">{task.title}</div>
                      {task.description && <div className="kanban-card-desc">{task.description}</div>}
                      {assignee && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                          <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', flexShrink: 0, overflow: 'hidden' }}>
                            {assignee.avatar ? <img src={assignee.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : assignee.displayName?.charAt(0)?.toUpperCase()}
                          </div>
                          {assignee.displayName}
                        </div>
                      )}
                      <div className="kanban-card-actions">
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => openTaskEditor(column.id, task)}
                          style={{ color: 'var(--accent)', padding: 4 }}
                          title="Edit task"
                        >
                          <PencilSquareIcon className="icon" />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleDeleteTask(column.id, task.id)}
                          style={{ color: 'var(--danger)', padding: 4 }}
                        >
                          <TrashIcon className="icon" />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
                <div className="kanban-add-card">
                  {showAddTask === column.id ? (
                    <div>
                      <input
                        className="form-input"
                        placeholder="Task title"
                        value={taskForm.title}
                        onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                        autoFocus
                        style={{ marginBottom: 8 }}
                      />
                      <input
                        className="form-input"
                        placeholder="Description (optional)"
                        value={taskForm.description}
                        onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                        style={{ marginBottom: 8 }}
                      />
                      <select
                        className="form-input"
                        value={taskForm.assigneeId}
                        onChange={e => setTaskForm({ ...taskForm, assigneeId: e.target.value })}
                        style={{ marginBottom: 8 }}
                      >
                        <option value="">No assignee</option>
                        {orgMembers.map(m => (
                          <option key={m._id} value={m._id}>{m.displayName}</option>
                        ))}
                      </select>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => handleAddTask(column.id)}>Add</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setShowAddTask(null); setTaskForm({ title: '', description: '', assigneeId: '' }); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button className="kanban-add-btn" onClick={() => setShowAddTask(column.id)}>
                      <PlusIcon className="icon" /> Add task
                    </button>
                  )}
                </div>
              </div>
            ))}

            {(activeBoard.columns?.length || 0) < maxColumns && (
              <div className="kanban-column" style={{ minHeight: 200, opacity: 0.7 }}>
                {showAddColumn ? (
                  <div style={{ padding: 8 }}>
                    <input
                      className="form-input"
                      placeholder="Column name"
                      value={newColumnTitle}
                      onChange={e => setNewColumnTitle(e.target.value)}
                      autoFocus
                      style={{ marginBottom: 8 }}
                      onKeyDown={e => e.key === 'Enter' && handleAddColumn()}
                    />
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-primary btn-sm" onClick={handleAddColumn}>Add</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setShowAddColumn(false); setNewColumnTitle(''); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="kanban-add-btn"
                    onClick={() => setShowAddColumn(true)}
                    style={{ padding: 16, textAlign: 'center', width: '100%' }}
                  >
                    <PlusIcon className="icon" /> Add Column ({activeBoard.columns?.length || 0}/{maxColumns})
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <Modal
        isOpen={showCreateBoard}
        onClose={() => setShowCreateBoard(false)}
        title={`Create ${boardScope === 'club' ? 'Club' : 'Personal'} Kanban Board`}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowCreateBoard(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreateBoard}>Create</button>
          </>
        }
      >
        <div className="form-group">
          <label>Board Name</label>
          <input
            className="form-input"
            value={boardTitle}
            onChange={e => setBoardTitle(e.target.value)}
            placeholder={boardScope === 'club' ? 'e.g. Club Operations' : 'e.g. My Tasks'}
            autoFocus
          />
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          The board will be created empty. Add up to {maxColumns} columns and organize your tasks.
        </div>
      </Modal>

      <Modal
        isOpen={!!showEditTask}
        onClose={() => setShowEditTask(null)}
        title="Edit Task"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowEditTask(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleEditTask}>Save</button>
          </>
        }
      >
        <div className="form-group">
          <label>Task Title</label>
          <input
            className="form-input"
            value={editTaskForm.title}
            onChange={e => setEditTaskForm({ ...editTaskForm, title: e.target.value })}
            autoFocus
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea
            className="form-input"
            rows={3}
            value={editTaskForm.description}
            onChange={e => setEditTaskForm({ ...editTaskForm, description: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Assignee</label>
          <select
            className="form-input"
            value={editTaskForm.assigneeId}
            onChange={e => setEditTaskForm({ ...editTaskForm, assigneeId: e.target.value })}
          >
            <option value="">No assignee</option>
            {orgMembers.map(m => (
              <option key={m._id} value={m._id}>{m.displayName}</option>
            ))}
          </select>
        </div>
      </Modal>
    </div>
  );
}
